'use strict';

import { logInfo, logWarn, logError } from '../core/bambuserLogger';
import { trimString } from '../core/bambuserFormatters';
import { httpPostForm } from '../core/bambuserApiClient';
import {
    bambuserProductIdToPid,
    nativeCartMirror,
    nativeCartBaseline,
    pendingHydrationByProductId,
    snapshotNativeCartBaseline,
} from '../core/bambuserSharedState';
import {
    getEventPid,
    getEventQuantity,
} from '../product/bambuserPidResolver';
import {
    fetchNativeCartState,
    invalidateCartStateCache,
    syncNativeCartMirrorFromState,
    syncNativeCartDecrease,
    getPidLineItemsFromCartState,
    getLineItemsQuantityTotal,
} from './bambuserCartSync';
import {
    hydrateProductForPlayer,
    resolveAddToCartPid,
} from '../product/bambuserProductHydration';
import * as bambuserMetrics from '../utils/bambuserMetrics';

// ---------------------------------------------------------------------------
// Cart → player helpers
// ---------------------------------------------------------------------------

function buildPlayerCartItems (cartState) {
    var items = [];
    var pidToLineItems =
        cartState && cartState.pidToLineItems && typeof cartState.pidToLineItems === 'object'
            ? cartState.pidToLineItems
            : {};

    for (var pid in pidToLineItems) {
        if (!Object.prototype.hasOwnProperty.call(pidToLineItems, pid)) {
            continue;
        }

        var lineItems = Array.isArray(pidToLineItems[pid]) ? pidToLineItems[pid] : [];
        var totalQty = 0;
        for (var i = 0; i < lineItems.length; i++) {
            totalQty += Number(lineItems[i] && lineItems[i].quantity) || 1;
        }

        var baselineQty = nativeCartBaseline[pid] || 0;
        var playerQty = totalQty - baselineQty;

        if (playerQty > 0) {
            items.push({ productId: pid, quantity: playerQty });
        }
    }

    return items;
}

function pushCartToPlayer (player, cartState) {
    if (typeof player.updateCart !== 'function') {
        return;
    }

    var items = buildPlayerCartItems(cartState);

    // The SDK only supports updateCart with an empty array (clearing).
    // For non-empty carts the player tracks state via ADD_TO_CART /
    // UPDATE_ITEM_IN_CART callbacks, so we only call updateCart to clear.
    if (items.length === 0) {
        logInfo('pushCartToPlayer clearing cart');
        player.updateCart({ items: [] });
    }
}

function updateMinicartDOM (doc, quantityTotal) {
    var container = doc.querySelector('.js-mainHeader-minicart');
    if (!container) {
        return;
    }

    var counter = container.querySelector('.js-mainHeader-counter_minicart');
    if (!counter) {
        return;
    }

    counter.textContent = String(quantityTotal);
    counter.classList.remove('hidden');

    if (quantityTotal > 0) {
        container.classList.add('cc-mainHeader-minicart_full');
    } else {
        container.classList.remove('cc-mainHeader-minicart_full');
    }
}

function triggerMinicartUpdate (quantityTotal) {
    try {
        // Update parent page DOM
        updateMinicartDOM(document, quantityTotal);

        // Update any Bambuser IFRAME navigation iframes
        var iframes = document.querySelectorAll('iframe');
        for (var i = 0; i < iframes.length; i++) {
            try {
                var iframeDoc = iframes[i].contentDocument || (iframes[i].contentWindow && iframes[i].contentWindow.document);
                if (iframeDoc) {
                    updateMinicartDOM(iframeDoc, quantityTotal);
                }
            } catch (crossOrigin) {
                // Skip cross-origin iframes
            }
        }
    } catch (e) {
        // Mini-cart update is best-effort; do not break cart flow.
    }
}

function broadcastCartRefresh () {
    var msg = { type: 'bambuser:refreshCart' };
    try {
        window.postMessage(msg, '*');
        var iframes = document.querySelectorAll('iframe');
        for (var i = 0; i < iframes.length; i++) {
            try {
                iframes[i].contentWindow.postMessage(msg, '*');
            } catch (e) { /* cross-origin */ }
        }
    } catch (e) { /* ignore */ }

    // The SDK may reset the iframe to live-shopping after the player
    // auto-minimizes. If the user was on the cart page, we need to
    // re-navigate the iframe back to cart once the SDK finishes its reset.
    if (broadcastCartRefresh._lastCartUrl) {
        ensureIframeOnCart(broadcastCartRefresh._lastCartUrl);
    }
}

/**
 * Track when the iframe is on the cart page so we can restore it after
 * the SDK resets it.
 */
broadcastCartRefresh._lastCartUrl = null;

function trackIframeCartUrl (config) {
    if (config && config.checkoutUrl) {
        var url = config.checkoutUrl;
        if (url && url.indexOf('http') !== 0) {
            url = window.location.origin +
                (url.charAt(0) === '/' ? '' : '/') + url;
        }
        broadcastCartRefresh._lastCartUrl = url;
    }
}

/**
 * After the SDK resets its iframe, re-navigate it to cart.
 * Polls for the iframe and waits for the SDK's load event before redirecting.
 */
function ensureIframeOnCart (cartUrl) {
    var attempts = 0;
    var maxAttempts = 30;
    var handled = false;
    var poll = setInterval(function () {
        attempts++;
        if (attempts >= maxAttempts || handled) {
            clearInterval(poll);
            return;
        }

        var iframes = document.querySelectorAll('iframe');
        for (var i = 0; i < iframes.length; i++) {
            try {
                var iframeWin = iframes[i].contentWindow;
                if (!iframeWin) continue;
                var iframeDoc = iframes[i].contentDocument ||
                    iframeWin.document;
                if (!iframeDoc ||
                    !iframeDoc.querySelector('.js-mainHeader-minicart')) {
                    continue;
                }

                // Already on cart — send refresh message and stop
                var href = '';
                try { href = iframeWin.location.href; } catch (e) {}
                if (href.indexOf('Cart-Show') !== -1) {
                    handled = true;
                    clearInterval(poll);
                    try {
                        iframeWin.postMessage(
                            { type: 'bambuser:refreshCart' }, '*');
                    } catch (e) {}
                    return;
                }

                // Iframe loaded something else (live-shopping) — redirect
                handled = true;
                clearInterval(poll);
                iframeWin.location.href = cartUrl;
                return;
            } catch (e) {
                // Skip cross-origin iframes
            }
        }
    }, 150);
}

function updateMinicartCount (responsePayload) {
    if (!responsePayload) {
        return;
    }

    var numItems;

    if (responsePayload.quantityTotal !== undefined && responsePayload.quantityTotal !== null) {
        numItems = responsePayload.quantityTotal;
    } else if (responsePayload.cart && responsePayload.cart.numItems !== undefined) {
        numItems = responsePayload.cart.numItems;
    } else if (responsePayload.cartModel && responsePayload.cartModel.numItems !== undefined) {
        numItems = responsePayload.cartModel.numItems;
    }

    if (typeof numItems === 'number') {
        triggerMinicartUpdate(numItems);
    }
}

// ---------------------------------------------------------------------------
// Bambuser shopper event tracking helpers
// ---------------------------------------------------------------------------

function getCartItemsFromResponse (response) {
    if (response && response.cart && response.cart.items) {
        return response.cart.items;
    }
    if (response && response.cartModel && response.cartModel.items) {
        return response.cartModel.items;
    }
    return null;
}

function findAddedCartItem (response, pid) {
    var items = getCartItemsFromResponse(response);
    if (!items) return null;

    // 1. Try UUID match
    var uuid = response && (response.pliUUID || response.pliuuid);
    if (uuid) {
        for (var i = 0; i < items.length; i++) {
            if ((items[i].uuid || items[i].UUID) === uuid) {
                return items[i];
            }
        }
    }

    // 2. Fallback: match by pid (exact, then normalized)
    if (pid) {
        var normalizedPid = pid.replace(/\s+/g, ' ').trim();
        for (var j = 0; j < items.length; j++) {
            var itemId = items[j].id || '';
            if (itemId === pid || itemId === normalizedPid ||
                itemId.replace(/\s+/g, ' ').trim() === normalizedPid) {
                return items[j];
            }
        }
    }

    return null;
}

function trackPlayerAddToCart (response, pid, quantity, config) {
    try {
        var currency = config.currency || 'EUR';
        var matched = findAddedCartItem(response, pid);

        if (matched) {
            var product = bambuserMetrics.mapProduct(matched, currency);
            product.quantity = quantity;
            bambuserMetrics.track('add-to-cart', { products: [product] });
        } else {
            bambuserMetrics.track('add-to-cart', { products: [{
                id: pid.split('_')[0].split(' ')[0],
                name: '',
                quantity: quantity,
                price: 0,
                currency: currency,
            }] });
        }
    } catch (e) {
        // Analytics must never break the cart flow
    }
}

function trackPlayerUpdateCart (pid, quantity, config) {
    try {
        var currency = config.currency || 'EUR';
        bambuserMetrics.track('update-cart', { products: [{
            id: pid.split('_')[0].split(' ')[0],
            name: '',
            quantity: quantity,
            price: 0,
            currency: currency,
        }] });
    } catch (e) {
        // Analytics must never break the cart flow
    }
}

function updateMinicartFromCartState (cartState) {
    var pidToLineItems =
        cartState && cartState.pidToLineItems && typeof cartState.pidToLineItems === 'object'
            ? cartState.pidToLineItems
            : {};
    var total = 0;

    for (var pid in pidToLineItems) {
        if (!Object.prototype.hasOwnProperty.call(pidToLineItems, pid)) {
            continue;
        }

        var lineItems = Array.isArray(pidToLineItems[pid]) ? pidToLineItems[pid] : [];
        for (var i = 0; i < lineItems.length; i++) {
            total += Number(lineItems[i] && lineItems[i].quantity) || 1;
        }
    }

    triggerMinicartUpdate(total);
}

// ---------------------------------------------------------------------------
// Callback helpers
// ---------------------------------------------------------------------------

function signalPlayerResult (done, result) {
    if (typeof done !== 'function') {
        return;
    }
    done(result);
}

function isOutOfStockError (error) {
    var payload = error && error.payload;
    if (payload && (payload.errorCode === 'OUT_OF_STOCK' || payload.errorCode === 'PRODUCT_NOT_FOUND')) {
        return true;
    }

    return false;
}

function getPlayerFailureResult (error) {
    var message = trimString(error && error.message);

    if (isOutOfStockError(error)) {
        return {
            success: false,
            reason: 'out-of-stock',
            message: message,
        };
    }

    if (message && !/^HTTP\s+\d+/i.test(message)) {
        return {
            success: false,
            reason: 'custom-error',
            message: message,
        };
    }

    return false;
}

// ---------------------------------------------------------------------------
// Player cart integration
// ---------------------------------------------------------------------------

export function configurePlayerCartIntegration (player, config) {
    if (!player) { return; }
    // Session counter: previous handlers self-deactivate when they detect
    // a newer session, so re-configuring the same player is always safe.
    var sessionId = (player.__intimissimiSessionId || 0) + 1;
    player.__intimissimiSessionId = sessionId;

    // Clean up the previous window message listener (cannot self-deactivate)
    if (player.__intimissimiCartChangedHandler) {
        window.removeEventListener('message', player.__intimissimiCartChangedHandler);
    }

    logInfo('configuring player cart integration', { sessionId: sessionId });

    if (typeof player.configure === 'function') {
        var configOptions = {
            locale: config.locale,
            currency: config.currency,
        };

        try {
            if (player.BUTTON && player.BUTTON.MINIMIZE) {
                configOptions.buttons = {
                    dismiss: player.BUTTON.MINIMIZE,
                };
            }

            if (
                player.FLOATING_PLAYER_NAVIGATION_MODE &&
                player.FLOATING_PLAYER_NAVIGATION_MODE.IFRAME
            ) {
                configOptions.floatingPlayer = {
                    navigationMode:
                        player.FLOATING_PLAYER_NAVIGATION_MODE.IFRAME,
                };
            }

            player.configure(configOptions);
        } catch (e) {
            logWarn('player.configure failed with miniplayer options', {
                message: e && e.message ? e.message : e,
            });
            // Retry with just locale/currency so events can still bind
            try {
                player.configure({
                    locale: config.locale,
                    currency: config.currency,
                });
            } catch (ignore) {
                // Continue to event binding regardless
            }
        }
    } else {
        logWarn('player.configure is unavailable');
    }

    // ----- PROVIDE_PRODUCT_DATA -----
    player.on(player.EVENT.PROVIDE_PRODUCT_DATA, function (event) {
        if (player.__intimissimiSessionId !== sessionId) { return; }
        var products =
            event && Array.isArray(event.products) ? event.products : [];

        logInfo('event PROVIDE_PRODUCT_DATA', {
            productsCount: products.length,
        });

        for (var i = 0; i < products.length; i++) {
            hydrateProductForPlayer(player, products[i], config);
        }
    });

    // ----- ADD_TO_CART -----
    player.on(player.EVENT.ADD_TO_CART, function (event, callback) {
        if (player.__intimissimiSessionId !== sessionId) { return; }
        var pendingKeys = Object.keys(pendingHydrationByProductId);
        if (pendingKeys.length > 0) {
            var pendingPromises = [];
            for (var pi = 0; pi < pendingKeys.length; pi++) {
                pendingPromises.push(
                    pendingHydrationByProductId[pendingKeys[pi]],
                );
            }
            var timeout = new Promise(function (resolve) {
                window.setTimeout(resolve, 10000);
            });
            Promise.race([Promise.all(pendingPromises), timeout]).then(
                function () {
                    handleAddToCart(event, callback);
                },
            );
            return;
        }
        handleAddToCart(event, callback);
    });

    function handleAddToCart (event, callback) {
        var initialPid = getEventPid(event);

        if (!initialPid) {
            var bambuserIdFallbacks = [
                event && trimString(event.productId),
                event && trimString(event.id),
                event && event.item && trimString(event.item.id),
                event && event.product && trimString(event.product.id),
            ];
            for (var bi = 0; bi < bambuserIdFallbacks.length; bi++) {
                var bmId = bambuserIdFallbacks[bi];
                if (bmId && bambuserProductIdToPid[bmId]) {
                    initialPid = bambuserProductIdToPid[bmId];
                    break;
                }
            }
        }

        var quantity = 1;

        logInfo('event ADD_TO_CART', { pid: initialPid });

        if (!initialPid || !config.addToCartUrl) {
            logWarn('ADD_TO_CART rejected: missing pid or addToCartUrl', {
                pid: initialPid,
                hasAddToCartUrl: !!config.addToCartUrl,
            });
            signalPlayerResult(callback, false);
            return;
        }

        resolveAddToCartPid(initialPid, event, config)
            .then(function (resolvedPid) {
                var pidToPost = resolvedPid || initialPid;

                return httpPostForm(config.addToCartUrl, {
                    pid: pidToPost,
                    quantity: quantity,
                }).then(function (addResponse) {
                    updateMinicartCount(addResponse);
                    trackPlayerAddToCart(addResponse, pidToPost, quantity, config);
                    invalidateCartStateCache();

                    return fetchNativeCartState(config)
                        .then(function (cartState) {
                            syncNativeCartMirrorFromState(cartState);
                            pushCartToPlayer(player, cartState);
                        })
                        .catch(function () {
                            nativeCartMirror[pidToPost] =
                                (nativeCartMirror[pidToPost] || 0) + quantity;
                        })
                        .then(function () {
                            broadcastCartRefresh();
                            signalPlayerResult(callback, true);
                        });
                });
            })
            .catch(function (error) {
                logError('ADD_TO_CART failed', {
                    pid: initialPid,
                    quantity: quantity,
                    message: error && error.message ? error.message : error,
                });
                signalPlayerResult(callback, getPlayerFailureResult(error));
            });
    }

    // ----- UPDATE_ITEM_IN_CART -----
    player.on(player.EVENT.UPDATE_ITEM_IN_CART, function (event, callback) {
        if (player.__intimissimiSessionId !== sessionId) { return; }
        var pid = getEventPid(event);
        var playerTargetQty = getEventQuantity(event, 1);

        if (!pid) {
            logWarn('UPDATE_ITEM_IN_CART rejected: missing pid');
            signalPlayerResult(callback, false);
            return;
        }

        fetchNativeCartState(config)
            .then(function (cartState) {
                syncNativeCartMirrorFromState(cartState);

                var pidState = getPidLineItemsFromCartState(cartState, pid);
                var effectivePid = pidState.pid || pid;
                var currentQty = getLineItemsQuantityTotal(pidState.lineItems);
                var baselineQty = nativeCartBaseline[effectivePid] || nativeCartBaseline[pid] || 0;
                var targetQty = baselineQty + playerTargetQty;
                var delta = targetQty - currentQty;

                if (delta <= 0) {
                    return syncNativeCartDecrease(
                        effectivePid,
                        targetQty,
                        config,
                        cartState,
                    ).then(function (syncResult) {
                        if (syncResult.quantity > 0) {
                            nativeCartMirror[syncResult.pid] =
                                syncResult.quantity;
                        } else {
                            delete nativeCartMirror[syncResult.pid];
                        }

                        return fetchNativeCartState(config)
                            .then(function (updatedState) {
                                syncNativeCartMirrorFromState(updatedState);
                                updateMinicartFromCartState(updatedState);
                                pushCartToPlayer(player, updatedState);
                            })
                            .catch(function () {})
                            .then(function () {
                                broadcastCartRefresh();
                                trackPlayerUpdateCart(effectivePid, playerTargetQty, config);
                                signalPlayerResult(callback, true);
                            });
                    });
                }

                if (!config.addToCartUrl) {
                    logWarn('UPDATE_ITEM_IN_CART rejected: missing addToCartUrl');
                    signalPlayerResult(callback, false);
                    return null;
                }

                return httpPostForm(config.addToCartUrl, {
                    pid: effectivePid,
                    quantity: delta,
                })
                    .then(function (updateResponse) {
                        updateMinicartCount(updateResponse);
                        invalidateCartStateCache();

                        return fetchNativeCartState(config)
                            .then(function (nextCartState) {
                                syncNativeCartMirrorFromState(nextCartState);
                                pushCartToPlayer(player, nextCartState);
                            })
                            .catch(function () {
                                nativeCartMirror[effectivePid] = targetQty;
                            });
                    })
                    .then(function () {
                        broadcastCartRefresh();
                        trackPlayerUpdateCart(effectivePid, playerTargetQty, config);
                        signalPlayerResult(callback, true);
                    });
            })
            .catch(function (stateError) {
                var baselineQty = nativeCartBaseline[pid] || 0;
                var targetQty = baselineQty + playerTargetQty;
                var currentQty = nativeCartMirror[pid] || 0;
                var delta = targetQty - currentQty;

                logWarn('UPDATE_ITEM_IN_CART using mirror fallback', {
                    pid: pid,
                    baselineQty: baselineQty,
                    targetQty: targetQty,
                    message:
                        stateError && stateError.message
                            ? stateError.message
                            : stateError,
                });

                if (delta <= 0) {
                    return syncNativeCartDecrease(pid, targetQty, config).then(
                        function (syncResult) {
                            if (syncResult.quantity > 0) {
                                nativeCartMirror[syncResult.pid] =
                                    syncResult.quantity;
                            } else {
                                delete nativeCartMirror[syncResult.pid];
                            }

                            return fetchNativeCartState(config)
                                .then(function (updatedState) {
                                    syncNativeCartMirrorFromState(updatedState);
                                    updateMinicartFromCartState(updatedState);
                                    pushCartToPlayer(player, updatedState);
                                })
                                .catch(function () {})
                                .then(function () {
                                    broadcastCartRefresh();
                                    trackPlayerUpdateCart(pid, playerTargetQty, config);
                                    signalPlayerResult(callback, true);
                                });
                        },
                    );
                }

                if (!config.addToCartUrl) {
                    signalPlayerResult(callback, false);
                    return null;
                }

                return httpPostForm(config.addToCartUrl, {
                    pid: pid,
                    quantity: delta,
                }).then(function (fallbackResponse) {
                    nativeCartMirror[pid] = targetQty;
                    updateMinicartCount(fallbackResponse);
                    invalidateCartStateCache();

                    fetchNativeCartState(config)
                        .then(function (updatedState) {
                            syncNativeCartMirrorFromState(updatedState);
                            pushCartToPlayer(player, updatedState);
                        })
                        .catch(function () {})
                        .then(function () {
                            broadcastCartRefresh();
                            trackPlayerUpdateCart(pid, playerTargetQty, config);
                            signalPlayerResult(callback, true);
                        });
                });
            })
            .catch(function (error) {
                logError('UPDATE_ITEM_IN_CART failed', {
                    pid: pid,
                    playerTargetQty: playerTargetQty,
                    message: error && error.message ? error.message : error,
                });
                signalPlayerResult(callback, getPlayerFailureResult(error));
            });
    });

    // ----- CHECKOUT -----
    player.on(player.EVENT.CHECKOUT, function () {
        if (player.__intimissimiSessionId !== sessionId) { return; }
        logInfo('event CHECKOUT');

        if (!config.checkoutUrl) {
            logWarn('CHECKOUT ignored: missing checkoutUrl');
            return;
        }

        var checkoutUrl = config.checkoutUrl;
        if (checkoutUrl && checkoutUrl.indexOf('http') !== 0) {
            checkoutUrl =
                window.location.origin +
                (checkoutUrl.charAt(0) === '/' ? '' : '/') +
                checkoutUrl;
        }

        // Minimize the player so it becomes a floating miniplayer,
        // then navigate inside the iframe so the player stays visible.
        if (typeof player.minimize === 'function') {
            try {
                player.minimize();
            } catch (e) {
                // When not available just redirect to the page
                logInfo('player.minimize() not supported, skipping');
            }
        }

        // Remember the cart URL so broadcastCartRefresh can restore it
        // if the SDK resets the iframe after a subsequent player interaction.
        trackIframeCartUrl(config);

        // Give the SDK time to set up the iframe after minimizing,
        // then navigate the merchant-page iframe (not the parent window).
        setTimeout(function () {
            var iframes = document.querySelectorAll('iframe');
            var navigated = false;

            for (var i = 0; i < iframes.length; i++) {
                try {
                    var iframeDoc = iframes[i].contentDocument ||
                        (iframes[i].contentWindow && iframes[i].contentWindow.document);
                    if (iframeDoc && iframeDoc.querySelector('.js-mainHeader-minicart')) {
                        iframes[i].contentWindow.location.href = checkoutUrl;
                        navigated = true;
                        break;
                    }
                } catch (e) {
                    // Skip cross-origin iframes
                }
            }

            if (!navigated) {
                // Fallback: inject a temporary link and click it so the SDK
                // can intercept the navigation in IFRAME mode.
                var a = document.createElement('a');
                a.href = checkoutUrl;
                a.style.display = 'none';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            }
        }, 300);
    });

    // ----- SYNC_CART_STATE -----
    player.on(player.EVENT.SYNC_CART_STATE, function () {
        if (player.__intimissimiSessionId !== sessionId) { return; }
        if (typeof player.updateCart !== 'function') { return; }

        fetchNativeCartState(config)
            .then(function (cartState) {
                syncNativeCartMirrorFromState(cartState);
                pushCartToPlayer(player, cartState);
            })
            .catch(function (error) {
                logWarn('SYNC_CART_STATE native cart check failed', {
                    message: error && error.message ? error.message : error,
                });

                if (Object.keys(nativeCartMirror).length === 0) {
                    player.updateCart({ items: [] });
                }
            });
    });

    // ----- Initial cart mirror sync -----
    fetchNativeCartState(config)
        .then(function (cartState) {
            syncNativeCartMirrorFromState(cartState);
            snapshotNativeCartBaseline();
        })
        .catch(function () {});

    // ----- Listen for native cart changes (from iframe navigation) -----
    // When the user modifies the cart directly (remove, quantity change,
    // coupon), clear the Bambuser player cart so it stays honest.
    // The SDK's updateCart() only supports empty arrays (clearing) if you
    // remove items. Clearing is the only safe reset available the time this
    // solution implemented.
    var onCartChanged = function (event) {
        if (player.__intimissimiSessionId !== sessionId) {
            window.removeEventListener('message', onCartChanged);
            return;
        }
        if (!event.data || event.data.type !== 'bambuser:cartChanged') {
            return;
        }

        logInfo('native cart changed (postMessage), clearing player cart');

        if (typeof player.updateCart === 'function') {
            player.updateCart({ items: [] });
        }

        // Re-snapshot baseline so future player additions are calculated
        // against the current native cart state.
        fetchNativeCartState(config)
            .then(function (cartState) {
                syncNativeCartMirrorFromState(cartState);
                snapshotNativeCartBaseline();
            })
            .catch(function () {});
    };
    player.__intimissimiCartChangedHandler = onCartChanged;
    window.addEventListener('message', onCartChanged);
}
